import EventHandler from '@pixelunion/events';

function loadData(el) {
  const dataEl = Array.from(el.children).find(child => 'componentData' in child.dataset);
  if (!dataEl) return {};

  // Load data from attribute, or innerHTML
  const data = dataEl.getAttribute('data-component-data') || dataEl.innerHTML;

  try {
    return JSON.parse(data);
  } catch (error) {
    throw new Error('Invalid component data found.', { cause: error });
  }
}

/**
 * @class Component
 * @classdesc Class representing a component.
 */
export default class Component {
  /**
   * @constructor
   * @param {HTMLElement} el - The component element.
   * @param {Object} context - The component's context.
   */
  constructor(el, context = {}) {
    this._el = el;
    this._context = context;
    this._state = {};
    this._data = loadData(el);
    this._eventHandler = new EventHandler();

    if (!this._el.hasAttribute('id')) {
      this._el.setAttribute(
        'id',
        // eslint-disable-next-line no-bitwise, space-infix-ops, no-mixed-operators
        ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)),
      );
    }
  }

  /**
   * Get the component's name.
   *
   * @memberof Component.prototype
   * @type {string} Component's name.
   */
  get name() {
    return this._el.dataset.component;
  }

  /**
   * Get the component's type.
   *
   * @memberof Component.prototype
   * @type {string} Component's type.
   */
  get type() {
    return this._el.dataset.componentType;
  }

  /**
   * Get the component's tag, if available.
   *
   * @memberof Component.prototype
   * @type {string|null} Component's tag, returns null if not present.
   */
  get tag() {
    return this._el.dataset.componentTag;
  }

  /**
   * Get the component's id.
   *
   * @memberof Component.prototype
   * @type {string} Component's id.
   */
  get id() {
    return this._el.id;
  }

  /**
   * Get the component's parent
   *
   * @memberof Component.prototype
   * @type {Component|null} Component's parent, returns null if no parent is found.
   */
  get parent() {
    const parentComponentEl = this._el.parentElement.closest('[data-component]');

    return parentComponentEl
      ? Core.Components.getComponent(parentComponentEl.getAttribute('id'))
      : null;
  }

  /**
   * Get the component's children
   *
   * @memberof Component.prototype
   * @type {Component[]} Component's children, returns an empty array if no children are found.
   */
  get children() {
    const children = [];
    const descendantComponentEls = this._el.querySelectorAll('[data-component]');

    for (let i = 0; i < descendantComponentEls.length; i++) {
      const descendantComponentEl = descendantComponentEls[i];
      const descendantComponent = Core.Components.getComponent(descendantComponentEl.getAttribute('id'));

      if (descendantComponent && descendantComponent.parent === this) {
        children.push(descendantComponent);
      }
    }

    return children;
  }

  /**
   * Get the component's element
   *
   * @memberof Component.prototype
   * @type {HTMLElement} Component's element.
   */
  get el() {
    return this._el;
  }

  /**
   * Get the component's context
   *
   * @memberof Component.prototype
   * @type {Object} Component's context.
   */
  get context() {
    return this._context;
  }

  /**
   * Get the component's data
   *
   * @memberof Component.prototype
   * @type {Object} Component's data.
   */
  get data() {
    return this._data;
  }

  /**
   * Get the component's state
   *
   * @memberof Component.prototype
   * @type {Object} Component's state.
   */
  get state() {
    return this._state;
  }

  /**
   * Set the component's state
   *
   * @memberof Component.prototype
   * @type {Object} Component's state.
   */
  set state(state) {
    this._state = state;
  }

  /**
   * Get the component's event handler
   *
   * @memberof Component.prototype
   * @type {EventHandler} Component's event handler.
   * @see {@link https://github.com/pixelunion/events|Events}
   */
  get events() {
    return this._eventHandler;
  }

  /**
   * Get the closest component with given parameters
   *
   * @memberof Component.prototype
   * @param {string} name - A component name
   * @param {string} [tag] - A component tag
   * @return {Component|null} The closest component with a given name and tag, null if not found.
   */
  closest(name, tag = null) {
    const result = this._el.closest(`[data-component="${name}"]${tag ? `[data-component-tag="${tag}"]` : ''}`);

    if (!result) return null;

    return Core.Components.getComponent(result.getAttribute('id'));
  }

  /**
   * Get first child component with given parameters
   *
   * @memberof Component.prototype
   * @param {string} name - A component name
   * @param {string} [tag] - A component tag
   * @return {Component|null} The first child component
   *                          with a given name and tag, null if not found.
   */
  query(name, tag = null) {
    const result = this._el.querySelector(`[data-component="${name}"]${tag ? `[data-component-tag="${tag}"]` : ''}`);

    if (!result) return null;

    return Core.Components.getComponent(result.getAttribute('id'));
  }

  /**
   * Get all children components with given parameters
   *
   * @memberof Component.prototype
   * @param {string} name - A component name
   * @param {string} [tag] - A component tag
   * @return {Component[]} An array of child components
   *                          with a given name and tag, an empty array if none are found.
   */
  queryAll(name, tag = null) {
    const results = [...this._el.querySelectorAll(`[data-component="${name}"]${tag ? `[data-component-tag="${tag}"]` : ''}`)];

    if (!results.length) return [];

    return results.reduce((acc, result) => {
      const component = Core.Components.getComponent(result.getAttribute('id'));

      if (component) {
        acc.push(component);
      }

      return acc;
    }, []);
  }

  /**
   * Set the state of the component
   *
   * @memberof Component.prototype
   * @param {Object} state - An object containing new state of the component
   */
  setState(state = {}) {
    const stateDiff = {};
    const stateKeys = Object.keys(state);

    // Shallow comparison
    for (let i = 0; i < stateKeys.length; i++) {
      const key = stateKeys[i];
      const value = state[key];

      if (!(key in this._state) || this._state[key] !== value) {
        stateDiff[key] = value;
      }
    }

    // Fast return if there's no shallow changes
    if (!Object.keys(stateDiff).length) return;

    this._state = {
      ...this._state,
      ...state,
    };

    const notifyChildren = parent => {
      const { children } = parent;

      for (let i = 0; i < children.length; i++) {
        const child = children[i];

        child._context = {
          ...this._context,
          ...this._state,
        };

        child.onContextChange(stateDiff);
        notifyChildren(child);
      }
    };

    notifyChildren(this);
    this.onStateChange(stateDiff);
  }

  /**
   * Called when the component is mounted to the DOM
   * all dependent components have been constructed.
   *
   * Initialization logic requiring interactions with other components
   * should be handled here.
   *
   * @memberof Component.prototype
   */
  onMount() {
    return undefined;
  }

  /**
   * Called when an ancenstral component changes state
   *
   * @memberof Component.prototype
   * @param {Object} newContext - An object representing the
   *                              new context derived from ancestral components
   */
  // eslint-disable-next-line no-unused-vars
  onContextChange(newContext) {
    return undefined;
  }

  /**
   * Called when this component changes state
   *
   * @memberof Component.prototype
   * @param {Object} newState - An object representing the
   *                            new state of the component
   */
  // eslint-disable-next-line no-unused-vars
  onStateChange(newState) {
    return undefined;
  }

  /**
   * Called when the component is dismounted.
   *
   * Destruction logic should be handled here.
   *
   * @memberof Component.prototype
   * @function
   */
  onDismount() {
    this._eventHandler.unregisterAll();
    this.unload();
  }

  /**
   * Called when the component is dismounted.
   *
   * @memberof Component.prototype
   * @deprecated Please update to onDismount
   */
  unload() {
    return undefined;
  }
}
